<template>
    <el-dialog
        :title="result ? $t('marketing.referral.bulk.result') : $t('marketing.referral.bulk_create')"
        :visible="visible"
        width="720px"
        top="5vh"
        append-to-body
        :close-on-click-modal="false"
        @update:visible="(v) => $emit('update:visible', v)"
    >
        <div data-testid="rf-bulk">
            <template v-if="!result">
                <div class="form-group">
                    <label class="form-control-label d-block">{{ $t("marketing.referral.bulk.mode") }}</label>
                    <el-radio-group v-model="bulk.mode" data-testid="rf-bulk-mode">
                        <el-radio label="per_owner">{{ $t("marketing.referral.bulk.mode_owner") }}</el-radio>
                        <el-radio label="count">{{ $t("marketing.referral.bulk.mode_count") }}</el-radio>
                    </el-radio-group>
                </div>

                <div v-if="bulk.mode === 'per_owner'" class="form-group">
                    <label class="form-control-label">{{ $t("marketing.referral.bulk.owners") }}</label>
                    <el-select
                        v-model="bulk.owner_bo_user_ids"
                        class="w-100"
                        multiple
                        filterable
                        remote
                        :remote-method="searchOwners"
                        :loading="ownersLoading"
                        :multiple-limit="200"
                        :placeholder="$t('marketing.referral.owner_placeholder')"
                        data-testid="rf-bulk-owners"
                        @visible-change="(o) => o && !owners.length && searchOwners('')"
                    >
                        <el-option v-for="o in owners" :key="o.id" :value="o.id" :label="o.nick_name ? o.name + ' (' + o.nick_name + ')' : o.name"></el-option>
                    </el-select>
                    <small v-if="errors.owner_bo_user_ids" class="text-danger">{{ err(errors.owner_bo_user_ids) }}</small>
                </div>
                <div v-else class="form-group">
                    <label class="form-control-label">{{ $t("marketing.referral.bulk.count") }}</label>
                    <el-input v-model="bulk.count" inputmode="numeric" maxlength="3" style="max-width: 160px;" data-testid="rf-bulk-count"></el-input>
                    <small v-if="errors.count" class="d-block text-danger">{{ err(errors.count) }}</small>
                </div>

                <div class="form-group">
                    <label class="form-control-label">{{ $t("marketing.referral.field.prefix") }}</label>
                    <el-input v-model="bulk.prefix" maxlength="8" class="rf-mono" style="max-width: 200px;" data-testid="rf-bulk-prefix" @input="bulk.prefix = clean(bulk.prefix)"></el-input>
                    <small class="d-block" :class="errors.prefix ? 'text-danger' : 'text-muted'">{{ $t("marketing.referral.prefix_hint") }}</small>
                </div>

                <referral-shared-fields
                    :form="form"
                    :errors="errors"
                    :teams="teams"
                    :campaigns="campaigns"
                    :lps="lps"
                    :templates="templates"
                ></referral-shared-fields>
            </template>

            <template v-else>
                <el-table :data="result.codes" size="small" class="bon-table bon-tight" max-height="420" data-testid="rf-bulk-result">
                    <el-table-column :label="$t('marketing.referral.col.code')" width="130">
                        <template v-slot="{ row }"><span class="rf-mono-text">{{ row.code }}</span></template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.col.owner')" min-width="140">
                        <template v-slot="{ row }">{{ row.owner_name || $t("marketing.referral.none_label") }}</template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.referral.col.link')" min-width="240">
                        <template v-slot="{ row }"><span class="small text-break">{{ row.link }}</span></template>
                    </el-table-column>
                    <el-table-column width="60" align="center">
                        <template v-slot="{ row }">
                            <base-button size="sm" type="neutral" class="px-2" :aria-label="$t('marketing.referral.copy_link')" @click="copy(row.link)"><i class="fa fa-copy"></i></base-button>
                        </template>
                    </el-table-column>
                </el-table>
            </template>
        </div>

        <span slot="footer">
            <template v-if="result">
                <base-button type="secondary" size="sm" data-testid="rf-bulk-csv" @click="downloadCsv"><i class="fa fa-file-csv"></i> {{ $t("marketing.referral.bulk.download_csv") }}</base-button>
                <base-button type="primary" size="sm" data-testid="rf-bulk-view" @click="viewInList">{{ $t("marketing.referral.bulk.view_in_list") }}</base-button>
            </template>
            <template v-else>
                <base-button type="secondary" size="sm" @click="$emit('update:visible', false)">{{ $t("marketing.referral.cancel") }}</base-button>
                <base-button type="primary" size="sm" :loading="saving" data-testid="rf-bulk-submit" @click="submit">{{ $t("marketing.referral.bulk.create") }}</base-button>
            </template>
        </span>
    </el-dialog>
</template>

<script>
import { Dialog, Select, Option, RadioGroup, Radio, Input, Table, TableColumn } from "element-ui";
import { ReferralCodeService, LandingPageService } from "@/resource";
import { apiErrorCode, isSchemaMissing } from "@/utils/marketing";
import { copyToClipboard } from "@/utils/clipboard";
import { bulkCsv, bulkPayload, cleanReferralInput, emptyCodeForm, fieldErrors, withIsoDates } from "@/utils/referral";
import ReferralSharedFields from "./ReferralSharedFields.vue";

// การตลาด → รหัสแนะนำ → สร้างหลายรหัส (Phase 2 spec 2026-10-13 §5.4).
// All or nothing: the API creates every code in one transaction with one batch_id.
export default {
    name: "ReferralBulkDialog",
    components: {
        [Dialog.name]: Dialog,
        [Select.name]: Select,
        [Option.name]: Option,
        [RadioGroup.name]: RadioGroup,
        [Radio.name]: Radio,
        [Input.name]: Input,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        ReferralSharedFields
    },
    props: {
        visible: { type: Boolean, default: false }
    },
    data() {
        return {
            bulk: { mode: "per_owner", owner_bo_user_ids: [], count: "10", prefix: "" },
            form: emptyCodeForm(),
            errors: {},
            saving: false,
            result: null,
            owners: [],
            ownersLoading: false,
            teams: [],
            campaigns: [],
            lps: [],
            templates: []
        };
    },
    watch: {
        visible: {
            immediate: true,
            handler(v) {
                if (v) this.open();
            }
        }
    },
    methods: {
        clean: cleanReferralInput,
        err(rule) {
            const k = "marketing.referral.err." + rule;
            return this.$te(k) ? this.$t(k) : this.$t("marketing.referral.err.format");
        },
        async open() {
            this.bulk = { mode: "per_owner", owner_bo_user_ids: [], count: "10", prefix: "" };
            this.form = emptyCodeForm();
            this.errors = {};
            this.result = null;
            const settle = (p) => p.then((r) => (r && r.data && r.data.data) || null).catch(() => null);
            const [meta, templates, lps] = await Promise.all([
                settle(ReferralCodeService.meta()),
                settle(ReferralCodeService.couponTemplates()),
                settle(LandingPageService.list({ status: "live", page: 1, limit: 100 }))
            ]);
            this.teams = (meta && meta.teams) || [];
            this.campaigns = (meta && meta.campaigns) || [];
            this.templates = templates || [];
            this.lps = (lps || []).filter((lp) => lp && lp.slug);
        },
        async searchOwners(q) {
            this.ownersLoading = true;
            try {
                const res = await ReferralCodeService.owners(q);
                const found = (res.data && res.data.data) || [];
                const keep = this.owners.filter((o) => this.bulk.owner_bo_user_ids.indexOf(o.id) !== -1 && !found.some((f) => f.id === o.id));
                this.owners = keep.concat(found);
            } catch (e) {
                /* keep the current list */
            } finally {
                this.ownersLoading = false;
            }
        },
        async submit() {
            const built = bulkPayload(this.bulk, withIsoDates(this.form));
            this.errors = built.ok ? {} : built.errors;
            if (!built.ok) return;
            this.saving = true;
            try {
                const res = await ReferralCodeService.bulk(built.payload);
                this.result = (res.data && res.data.data) || { batch_id: "", codes: [] };
                this.$notify({ type: "success", message: this.$t("marketing.referral.bulk.created_n", { n: this.result.codes.length }) });
                this.$emit("created", this.result);
            } catch (err) {
                const code = apiErrorCode(err);
                if (code === "invalid_body") this.errors = fieldErrors(err);
                else if (code === "too_many") this.errors = this.bulk.mode === "count" ? { count: "too_many" } : { owner_bo_user_ids: "too_many" };
                else if (code === "template_invalid") this.errors = { reward_coupon_template_id: "template_invalid" };
                else if (code === "invalid_landing") this.errors = { landing_path: "invalid_landing" };
                else this.$notify({ type: "danger", message: isSchemaMissing(err) ? this.$t("marketing.schema_missing") : this.$t("marketing.referral.err.generic") });
            } finally {
                this.saving = false;
            }
        },
        async copy(link) {
            const ok = await copyToClipboard(link);
            this.$notify({ type: ok ? "success" : "danger", message: ok ? this.$t("marketing.referral.copied") : this.$t("marketing.referral.err.generic") });
        },
        downloadCsv() {
            if (!this.result) return;
            const headers = ["code", "name", "owner", "link"].map((h) => this.$t("marketing.referral.col." + h));
            const blob = new Blob([bulkCsv(this.result.codes, headers)], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `referral-codes-${this.result.batch_id || "bulk"}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
        viewInList() {
            this.$emit("view-batch", this.result && this.result.batch_id);
            this.$emit("update:visible", false);
        }
    }
};
</script>

<style scoped>
.rf-mono >>> input,
.rf-mono-text {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    letter-spacing: 0.06em;
}
</style>
